import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export function Modal({
  title,
  onClose,
  children,
  maxWidth = 'max-w-lg',
  // Stacked-modal support: callers can bump the z-index so a "drilled-in"
  // detail (e.g. clicking GRN inside a PO modal) renders above the parent.
  // Defaults to 50 — the existing single-modal value.
  zIndex = 50,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
  zIndex?: number;
}) {
  // Portal to <body>. Otherwise `fixed inset-0` is NOT relative to the viewport
  // whenever an ancestor establishes a containing block for fixed descendants —
  // which our scroll-reveal wrappers do via `will-change: transform` (see the
  // `[data-reveal]` rules in index.css). Inside such a wrapper the overlay
  // centered against the full-height page instead of the screen, so a modal
  // opened after scrolling appeared far down the list. Rendering into <body>
  // escapes every transformed/will-change ancestor and restores true viewport
  // centering for every modal that uses this component.
  const overlay = (
    <div
      className="scrim-in fixed inset-0 bg-black/30 flex items-center justify-center p-4"
      style={{ zIndex }}
    >
      {/* dvh, not vh: on a phone 90vh is measured with the URL bar hidden, so
          the bottom of a tall form (its Save button) sat under the browser
          chrome. overscroll-contain stops a long form dragging the page. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`modal-in card w-full ${maxWidth} max-h-[90dvh] overflow-y-auto overscroll-contain`}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="iconbtn bare -mr-2 h-8 w-8 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
      </div>
    </div>
  );
  return typeof document !== 'undefined'
    ? createPortal(overlay, document.body)
    : overlay;
}
